import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Comment as CommentIcon,
  ContentCopy as CopyIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  LightbulbOutlined as SuggestionIcon,
} from '@mui/icons-material';
import { Box, Chip, Collapse, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { useJotCoachTracking } from '@services/statsig';
import { useModal } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { MODAL_TYPES } from '../../../components/modals/constants';
import type { JotCoachGap } from '../store/hooks';

interface GapItemProps {
  gap: JotCoachGap;
  sessionId?: string;
  noteId?: string;
  sectionId?: string;
  onApplyEdit?: (gap: JotCoachGap) => void;
}

export const GapItem: React.FC<GapItemProps> = ({
  gap,
  sessionId,
  noteId,
  sectionId,
  onApplyEdit,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { open } = useModal();
  const {
    trackGapRendered,
    trackGapViewed,
    trackGapExpanded,
    trackGapCollapsed,
    trackGapCopied,
    trackFeedbackUseful,
    trackFeedbackNotUseful,
    trackFeedbackComment,
  } = useJotCoachTracking();

  // Refs for tracking deduplication
  const hasBeenRenderedRef = useRef(false);
  const hasBeenViewedRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Track render when card enters viewport
  useEffect(() => {
    if (!sessionId || !noteId || !sectionId || hasBeenRenderedRef.current || !cardRef.current)
      return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasBeenRenderedRef.current) {
            trackGapRendered({
              jotcoach_session_id: sessionId,
              note_id: noteId,
              gap_id: gap.id,
              section_id: sectionId,
              priority: gap.priority,
            });
            hasBeenRenderedRef.current = true;
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, [sessionId, noteId, sectionId, gap.id, gap.priority, gap.title, trackGapRendered]);

  // Track hover/view
  const handleMouseEnter = useCallback(() => {
    if (!sessionId || !noteId || !sectionId || hasBeenViewedRef.current) return;

    trackGapViewed({
      jotcoach_session_id: sessionId,
      note_id: noteId,
      gap_id: gap.id,
      section_id: sectionId,
      priority: gap.priority,
      gap_title: gap.title,
    });
    hasBeenViewedRef.current = true;
  }, [sessionId, noteId, sectionId, gap.id, gap.priority, gap.title, trackGapViewed]);

  // Get border color based on priority
  const getBorderColor = () => {
    if (gap.priority === 'High') return 'error.light';
    if (gap.priority === 'Medium') return 'warning.light';
    return '#fdd835'; // Yellow for low priority
  };

  const getHoverBorderColor = () => {
    if (gap.priority === 'High') return 'error.main';
    if (gap.priority === 'Medium') return 'warning.main';
    return '#f9a825'; // Darker yellow for low priority
  };

  // Track expand/collapse
  const handleToggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const newExpanded = !expanded;
      setExpanded(newExpanded);

      if (!sessionId || !noteId || !sectionId) return;

      if (newExpanded) {
        trackGapExpanded({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          gap_id: gap.id,
          section_id: sectionId,
          priority: gap.priority,
          gap_title: gap.title,
        });
      } else {
        trackGapCollapsed({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          gap_id: gap.id,
          section_id: sectionId,
          priority: gap.priority,
          gap_title: gap.title,
        });
      }
    },
    [
      expanded,
      sessionId,
      noteId,
      sectionId,
      gap.id,
      gap.priority,
      gap.title,
      trackGapExpanded,
      trackGapCollapsed,
    ],
  );

  const handleCopyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(gap.suggested_edit.content);
      enqueueSnackbar('Copied to clipboard', { variant: 'success' });

      if (sessionId && noteId && sectionId) {
        trackGapCopied({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          gap_id: gap.id,
          section_id: sectionId,
          priority: gap.priority,
          gap_title: gap.title,
        });
      }
    } catch (err) {
      enqueueSnackbar('Failed to copy', { variant: 'error' });
    }
  }, [
    gap.suggested_edit.content,
    gap.id,
    gap.priority,
    gap.title,
    sessionId,
    noteId,
    sectionId,
    enqueueSnackbar,
    trackGapCopied,
  ]);

  // Track feedback clicks
  const handleFeedbackUseful = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (sessionId && noteId && sectionId) {
        trackFeedbackUseful({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          item_type: 'gap',
          item_id: gap.id,
          section_id: sectionId,
        });
      }
      open(MODAL_TYPES.JOTCOACH_FEEDBACK, {
        itemType: 'gap',
        itemId: gap.id,
        sessionId,
        noteId,
        sectionId,
        initialUseful: true,
        itemContent: {
          title: gap.title,
          description: gap.description,
        },
      });
    },
    [sessionId, noteId, sectionId, gap.id, gap.title, gap.description, trackFeedbackUseful, open],
  );

  const handleFeedbackNotUseful = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (sessionId && noteId && sectionId) {
        trackFeedbackNotUseful({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          item_type: 'gap',
          item_id: gap.id,
          section_id: sectionId,
        });
      }
      open(MODAL_TYPES.JOTCOACH_FEEDBACK, {
        itemType: 'gap',
        itemId: gap.id,
        sessionId,
        noteId,
        sectionId,
        initialUseful: false,
        itemContent: {
          title: gap.title,
          description: gap.description,
        },
      });
    },
    [
      sessionId,
      noteId,
      sectionId,
      gap.id,
      gap.title,
      gap.description,
      trackFeedbackNotUseful,
      open,
    ],
  );

  const handleFeedbackComment = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (sessionId && noteId && sectionId) {
        trackFeedbackComment({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          item_type: 'gap',
          item_id: gap.id,
          section_id: sectionId,
        });
      }
      open(MODAL_TYPES.JOTCOACH_FEEDBACK, {
        itemType: 'gap',
        itemId: gap.id,
        sessionId,
        noteId,
        sectionId,
        initialUseful: null,
        itemContent: {
          title: gap.title,
          description: gap.description,
        },
      });
    },
    [sessionId, noteId, sectionId, gap.id, gap.title, gap.description, trackFeedbackComment, open],
  );

  return (
    <Box
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: getBorderColor(),
        borderLeftWidth: 4,
        '&:hover': {
          borderColor: getHoverBorderColor(),
          boxShadow: 1,
        },
        transition: 'all 0.2s ease',
        cursor: expanded ? 'default' : 'pointer',
        position: 'relative',
      }}
      onClick={() => !expanded && setExpanded(true)}
    >
      {/* Feedback buttons */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          gap: 0.5,
          zIndex: 1,
        }}
      >
        <Tooltip title="Mark as useful">
          <IconButton
            size="small"
            onClick={handleFeedbackUseful}
            sx={{
              p: 0.5,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'success.light', color: 'success.dark' },
            }}
          >
            <CheckIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Mark as not useful">
          <IconButton
            size="small"
            onClick={handleFeedbackNotUseful}
            sx={{
              p: 0.5,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'error.light', color: 'error.dark' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add feedback">
          <IconButton
            size="small"
            onClick={handleFeedbackComment}
            sx={{
              p: 0.5,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'info.light', color: 'info.dark' },
            }}
          >
            <CommentIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Header */}
      <Box sx={{ pr: 7 }}>
        {/* Impact Chip with tooltip */}
        <Tooltip title={gap.reason} placement="top" arrow>
          <Chip
            label={gap.impact}
            size="small"
            variant="outlined"
            color={gap.impact.includes('Denial') ? 'error' : 'warning'}
            sx={{ mb: 0.5 }}
          />
        </Tooltip>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {gap.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {gap.description}
        </Typography>
      </Box>

      {/* Expand/Collapse toggle at bottom right */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <IconButton size="small" onClick={handleToggleExpand} sx={{ p: 0.5 }}>
          {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Suggested Edit - Collapsed by default */}
      <Collapse in={expanded}>
        <Divider sx={{ my: 1.5 }} />

        <Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SuggestionIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.75rem' }}
              >
                Suggested Edit
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleCopyToClipboard} sx={{ p: 0.5 }}>
              <CopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              bgcolor: '#e3f2fd',
              p: 1.5,
              borderRadius: 1,
              borderLeft: '3px solid',
              borderColor: 'primary.main',
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
              {gap.suggested_edit.content}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
